/**
 * One run of the signed-in recording's upload, from pre-flight to a terminal
 * state: a session (fresh, or the one the take was streamed into), the
 * resumable chunk upload, then `finalize`.
 *
 * A run publishes only while it is the task's current run, so a superseded run
 * is silent. It emits the two terminal telemetry events itself, because the
 * run outlives every screen that could otherwise emit them — and they are the
 * only record a client-side upload failure leaves: the API logs a successful
 * session creation and then silence, and the pending object the bytes landed
 * in is erased by the bucket lifecycle rule two days later.
 */
import {
  UPLOAD_CHUNK_STALL_TIMEOUT_MS,
  UPLOAD_FINALIZE_MAX_ATTEMPTS,
  UPLOAD_SESSION_MAX_ATTEMPTS,
  type UploadTransportReason,
} from "@reflect/domain";
import { GENERIC_ERROR_MESSAGE_KEYS, type ErrorMessageKey } from "@reflect/i18n";

import { fireFirstReflectionConversion } from "../adPixels/conversions";
import { trackEvent, type TrackFn } from "../analytics/track";
import { errorMessageKey, errorRequestId } from "../errors";
import type { LiveUpload } from "./liveUpload";
import { finalizeTerminalCanRetry, isAbortReason, runMutation } from "./mutationRetry";
import { networkType } from "./networkType";
import { PREFLIGHT_MESSAGE_KEYS, preflightRecording } from "./preflight";
import { patch, task } from "./reflectionUploadStore";
import { abortableSleep, runResumableUpload, throwIfAborted } from "./resumable";
import type { DriverFailure } from "./resumable";
import type {
  NowFn,
  RecordMessageKey,
  StartOptions,
  UploadFailureKind,
  UploadStatus,
} from "./types";
import { UploadAbortedError } from "./types";

/**
 * One run's identity, clocks and diagnostics.
 *
 * The diagnostics accumulate as the run learns them, and are what the `failed`
 * status and the `upload_failed` event carry out: which failure it was, whether
 * any bytes had committed, and which orphaned object it belongs to.
 */
interface RunContext {
  /** The recording this run uploads. */
  readonly recording: Blob;
  /** The task's `runId` when this run began. */
  readonly runId: number;
  /** The wall clock the run's durations are measured on. */
  readonly now: NowFn;
  /** The analytics sink the terminal events go to. */
  readonly track: TrackFn;
  /**
   * When the run began, taken before anything else so `durationMs` is the whole
   * wait: session creation, every chunk, every backoff and finalize.
   */
  readonly startedAt: number;
  /** The pending object key, once a session exists. */
  objectKey: string | null;
  /** The driver's high-water committed offset. */
  bytesCommitted: number;
  /**
   * Bytes the server had already acknowledged when the take ended — what
   * streaming saved the reader from waiting on. Absent when nothing was handed
   * over: every run that did not stream, and every run whose stream fell back
   * to a fresh session.
   */
  streamedBytes: number | undefined;
  /** The recording's size, once it is known to be a video. */
  declaredTotalBytes: number | null;
  /**
   * Consecutive attempts burned on the operation that gave up. It resets on
   * progress, so it is not the run's retry count; `retryCount` is.
   */
  lastAttempt: number;
  /** Retryable transport outcomes across the whole run. */
  retryCount: number;
  /** The subset of `retryCount` that were stalls or timeouts. */
  stallCount: number;
}

/** A terminal failure, as {@link setFailed} publishes and reports it. */
interface RunFailure {
  /** The machine-readable cause, for telemetry. */
  kind: UploadFailureKind;
  /** The catalog key the failure renders as. */
  messageKey: ErrorMessageKey | RecordMessageKey;
  /** Whether the user is offered a Retry. */
  canRetry: boolean;
  /** The server's request id, when a mutation answered with one. */
  requestId?: string | null;
  /** Developer-facing detail; never rendered. */
  detail?: string;
  /** The last transport outcome of an exhausted chunk upload. */
  transportVerdict?: { reason?: UploadTransportReason; status?: number };
}

/** Whether `run` is still the task's current run, and so may publish. */
function isCurrent(run: RunContext): boolean {
  return task.runId === run.runId;
}

/** Publish `next` as the task's status, if `run` is still current. */
function setStatus(run: RunContext, next: UploadStatus): void {
  if (isCurrent(run)) patch({ status: next });
}

/** Advance the display progress, and the stall clock with it. */
function reportProgress(run: RunContext, value: number): void {
  // The high-water mark spans runs of one recording; `start()`, `retry()` and
  // the reset clear it, so a second upload's bar does not render full from its
  // first byte.
  const advanced = value > task.progressHighWater;
  task.progressHighWater = Math.max(task.progressHighWater, value);
  if (!isCurrent(run)) return;
  // The hard-stall watchdog's clock, stamped only when the run got measurably
  // **closer to done**.
  //
  // The high-water mark is the right signal and the committed offset is not,
  // for two reasons that pull in opposite directions. Most recordings fit in a
  // single chunk, whose committed offset goes 0 → total in one step at the
  // very end — so a committed-offset clock would never tick during the only
  // part of the upload a user waits through, and a watchdog reading it would
  // declare every healthy single-chunk upload on a slow link stalled. And a
  // retry loop grinding against a dead socket re-sends the *same* chunk, which
  // cannot raise a monotonic high-water mark however many times it tries —
  // which is precisely the state worth naming.
  patch({
    progress: task.progressHighWater,
    ...(advanced ? { lastProgressAt: run.now() } : {}),
  });
}

/** Count one retryable transport outcome. */
function countRetry(run: RunContext, reason: UploadTransportReason): void {
  run.retryCount += 1;
  if (reason === "stall" || reason === "timeout") run.stallCount += 1;
}

/** The fields every terminal upload event stamps. */
function eventBase(run: RunContext): {
  file_size_kb: number;
  mime_type: string;
  network_type: string;
  streamed_bytes?: number;
} {
  return {
    file_size_kb: Math.round(run.recording.size / 1000),
    mime_type: run.recording.type,
    network_type: networkType(),
    ...(run.streamedBytes === undefined ? {} : { streamed_bytes: run.streamedBytes }),
  };
}

/** End the run as `failed`, and report it with its diagnostics. */
function setFailed(run: RunContext, failure: RunFailure): void {
  const { kind, messageKey, canRetry, requestId = null, detail, transportVerdict } = failure;
  const durationMs = run.now() - run.startedAt;
  setStatus(run, {
    phase: "failed",
    messageKey,
    requestId,
    canRetry,
    failureKind: kind,
    bytesCommitted: run.bytesCommitted,
    attempt: run.lastAttempt,
    durationMs,
    retryCount: run.retryCount,
    stallCount: run.stallCount,
    ...(detail ? { detail } : {}),
    ...(run.declaredTotalBytes === null ? {} : { totalBytes: run.declaredTotalBytes }),
    ...(run.objectKey === null ? {} : { objectKey: run.objectKey }),
    ...(transportVerdict?.reason === undefined
      ? {}
      : { lastTransportReason: transportVerdict.reason }),
    ...(transportVerdict?.status === undefined ? {} : { lastHttpStatus: transportVerdict.status }),
  });
  if (!isCurrent(run)) return;
  // `last_transport_reason` is the prop that names an outage. Every retryable
  // outcome ends the run as `failure_kind: "exhausted"` — a CORS-blocked PUT,
  // a mobile timeout and five 503s alike — and those are three different
  // investigations.
  run.track("upload_failed", {
    ...eventBase(run),
    failure_kind: kind,
    // The run-wide count, the same one `upload_succeeded` sends. `lastAttempt`
    // resets on progress and counts mutation attempts too, so `stall_count`
    // could exceed it.
    retry_count: run.retryCount,
    bytes_committed: run.bytesCommitted,
    total_bytes: run.declaredTotalBytes ?? undefined,
    object_key: run.objectKey ?? undefined,
    last_transport_reason: transportVerdict?.reason,
    last_http_status: transportVerdict?.status,
    duration_ms: durationMs,
    stall_count: run.stallCount,
  });
}

/** End the run as `failed` for a chunk-upload failure the driver gave up on. */
function mapDriverFailure(run: RunContext, failure: DriverFailure): void {
  const messageKey = GENERIC_ERROR_MESSAGE_KEYS.unknown;
  switch (failure.kind) {
    case "session-gone":
      // Terminal for the driver, retryable for the user: `retry()` mints a
      // fresh session.
      setFailed(run, {
        kind: "session-gone",
        messageKey: "web.record.error.sessionGone",
        canRetry: true,
      });
      return;
    case "client-error":
      setFailed(run, {
        kind: "client-error",
        messageKey,
        canRetry: false,
        detail: `HTTP ${failure.status}`,
      });
      return;
    case "protocol-error":
      setFailed(run, {
        kind: "protocol-error",
        messageKey,
        canRetry: false,
        detail: failure.detail,
      });
      return;
    case "offset-unknown":
      // Same user-facing affordance as `exhausted` — a fresh session is at
      // least worth one try — but a distinct developer-facing detail, so the
      // telemetry names the condition rather than reporting a generic stall.
      setFailed(run, {
        kind: "offset-unknown",
        messageKey,
        canRetry: true,
        detail: "308 resume offset unreadable (no Range on the response)",
      });
      return;
    case "exhausted":
      setFailed(run, {
        kind: "exhausted",
        messageKey,
        canRetry: true,
        detail:
          failure.lastReason === undefined
            ? undefined
            : `last transport verdict: ${failure.lastReason}${
                failure.lastStatus === undefined ? " (status 0)" : ` HTTP ${failure.lastStatus}`
              }`,
        transportVerdict: { reason: failure.lastReason, status: failure.lastStatus },
      });
      return;
  }
}

/**
 * Run one upload of `recording` to a terminal state, as the task's current run.
 *
 * Aborts any run still in flight first. Without that, a second `retry()` or
 * `start()` in the same tick would start an independent run: the run id
 * silences the superseded run's UI, but its session creation and finalize
 * would still execute and mint a duplicate reflection. The aborted run stops
 * at the driver's abort check, before it can finalize.
 *
 * @param recording - the assembled take.
 * @param options - duration and title, forwarded to both mutations.
 * @param source - the take's live stream, handed over by `start()`, or `null`.
 */
export async function performUpload(
  recording: Blob,
  options: StartOptions,
  source: LiveUpload | null,
): Promise<void> {
  const deps = task.deps;
  /* c8 ignore next -- every caller sets `task.deps` in the same turn it calls this. */
  if (deps === null) return;
  const sleep = deps.sleep ?? abortableSleep;
  const now = deps.now ?? Date.now;
  const startedAt = now();

  task.controller?.abort(new UploadAbortedError());
  const ownController = new AbortController();
  task.controller = ownController;
  const { signal } = ownController;
  // Aborting the run abandons the stream it inherited, so a reset mid-handoff
  // cannot leave a chunk PUT running against a take nobody wants.
  if (source !== null) signal.addEventListener("abort", () => source.abandon(), { once: true });
  task.runId += 1;
  const run: RunContext = {
    recording,
    runId: task.runId,
    now,
    track: deps.track ?? trackEvent,
    startedAt,
    objectKey: null,
    bytesCommitted: 0,
    streamedBytes: undefined,
    declaredTotalBytes: null,
    lastAttempt: 0,
    retryCount: 0,
    stallCount: 0,
  };

  try {
    const preflight = preflightRecording(recording, deps.maxUploadBytes);
    const totalBytes = recording.size;
    // The size is published once the recording is known to be a video, so an
    // over-cap refusal still reports how large it was.
    if (preflight.ok || preflight.refusal === "too-large") {
      run.declaredTotalBytes = totalBytes;
      if (isCurrent(run)) patch({ totalBytes });
    }
    if (!preflight.ok) {
      // A refused recording abandons its stream too: nothing streamed can be
      // finalized without it.
      source?.abandon();
      setFailed(run, {
        kind: preflight.refusal,
        messageKey: PREFLIGHT_MESSAGE_KEYS[preflight.refusal],
        canRetry: false,
      });
      return;
    }
    const { contentType } = preflight;

    // Take over the session the take was streamed into, if it is one worth
    // having. `close()` waits for the chunk in flight and never throws; `null`
    // means start from scratch, exactly as a take that never streamed.
    let handoff = null;
    if (source !== null) {
      setStatus(run, { phase: "uploading", bytesCommitted: 0, totalBytes, attempt: 0 });
      handoff = await source.close();
      throwIfAborted(signal);
      // A stream for another container, or one that somehow holds the whole
      // recording without having committed it, is not resumed. The second
      // cannot happen (the stream always leaves the tail) and is checked
      // because the final PUT is what closes the upload.
      if (
        handoff !== null &&
        (handoff.contentType !== contentType ||
          (handoff.committed !== null && handoff.committed >= totalBytes))
      ) {
        handoff = null;
      }
    }

    let session: { uploadUrl: string; key: string };
    if (handoff !== null) {
      session = handoff;
      // A short take hands over at 0; that is "nothing streamed", which the
      // telemetry spells as an absent prop rather than a zero.
      run.streamedBytes =
        handoff.committed === null || handoff.committed === 0 ? undefined : handoff.committed;
      run.bytesCommitted = handoff.committed ?? 0;
    } else {
      const creation = await runMutation({
        call: () =>
          deps.uploadApi.createResumableSession({
            contentType,
            sizeBytes: totalBytes,
          }),
        maxAttempts: UPLOAD_SESSION_MAX_ATTEMPTS,
        onAttempt: (attempt) => {
          run.lastAttempt = attempt;
          setStatus(run, { phase: "creating", attempt });
        },
        signal,
        sleep,
      });
      if (!creation.ok) {
        // A refused creation offers no retry; only exhaustion does.
        setFailed(run, {
          kind: "session-create",
          messageKey: errorMessageKey(creation.error),
          requestId: errorRequestId(creation.error),
          canRetry: creation.exhausted,
        });
        return;
      }
      session = creation.value;
    }
    run.objectKey = session.key;

    setStatus(run, {
      phase: "uploading",
      bytesCommitted: run.bytesCommitted,
      totalBytes,
      attempt: 0,
    });
    const outcome = await runResumableUpload({
      url: session.uploadUrl,
      blob: recording,
      totalBytes,
      // Resume where the stream left off: at the offset the server last
      // acknowledged, or with a probe when its last answer was not legible.
      ...(handoff === null
        ? {}
        : handoff.committed === null
          ? { probeFirst: true }
          : { initialOffset: handoff.committed }),
      chunkSizeBytes: deps.chunkSizeBytes,
      // Explicit rather than left to the transport's default, so an injected
      // transport sees the same stall window XHR applies.
      stallTimeoutMs: UPLOAD_CHUNK_STALL_TIMEOUT_MS,
      transport: deps.transport,
      signal,
      sleep,
      onPhase: (update) => {
        // The driver's high-water offset is the only place this is known, and
        // it is what tells a failure with no byte on the server apart from one
        // that stalled at 60%.
        run.bytesCommitted = update.bytesCommitted;
        run.lastAttempt = update.attempt;
        setStatus(run, {
          phase: update.phase,
          bytesCommitted: update.bytesCommitted,
          totalBytes: update.totalBytes,
          attempt: update.attempt,
        });
      },
      onProgress: (value) => reportProgress(run, value),
      onRetryable: (reason) => countRetry(run, reason),
    });
    if (outcome.kind === "failed") {
      mapDriverFailure(run, outcome.failure);
      return;
    }
    // The driver resolves `complete` only once the server has acknowledged the
    // whole object, so anything that fails from here on failed with every byte
    // already committed.
    run.bytesCommitted = totalBytes;

    // Finalize is idempotent, so replaying it is safe.
    const finalize = await runMutation({
      call: () =>
        deps.uploadApi.finalize({
          key: session.key,
          durationSeconds: options.durationSeconds,
          title: options.title,
          welcome: options.welcome,
        }),
      maxAttempts: UPLOAD_FINALIZE_MAX_ATTEMPTS,
      onAttempt: (attempt) => {
        run.lastAttempt = attempt;
        setStatus(run, { phase: "finalizing", totalBytes, attempt });
      },
      signal,
      sleep,
    });
    if (!finalize.ok) {
      setFailed(run, {
        kind: "finalize",
        messageKey: errorMessageKey(finalize.error),
        requestId: errorRequestId(finalize.error),
        canRetry: finalize.exhausted ? true : finalizeTerminalCanRetry(finalize.error),
      });
      return;
    }
    // Invalidated through the run's own callback rather than at each screen's
    // success handler: `/app/record` holds its usage query at `staleTime:
    // Infinity` so a mid-recording refetch cannot unmount the recorder, which
    // means nothing else would ever refresh it — and "Record another" would
    // hand a user who just spent their last slot a recorder whose upload the
    // server is now certain to refuse. The one place a finalize can succeed is
    // the one place this cannot be forgotten.
    deps.onFinalized?.();
    // Before the `isCurrent` guard below, like the invalidation above: a
    // superseded run's finalize still committed the reflection, and the
    // server answers `true` for one save per account only (card #1062).
    if (finalize.value.firstReflection) {
      fireFirstReflectionConversion(finalize.value.reflectionId);
    }
    const durationMs = now() - startedAt;
    setStatus(run, {
      phase: "succeeded",
      reflectionId: finalize.value.reflectionId,
      processingStatus: finalize.value.processingStatus,
      durationMs,
      retryCount: run.retryCount,
      stallCount: run.stallCount,
    });
    if (!isCurrent(run)) return;
    // Let the recording go. A success is the one terminal state nothing can be
    // retried from, so the blob is dead weight from here — and it is a whole
    // video. Nothing else would release it: a reader who takes the handover
    // and then walks to `/app/account` leaves a finished upload with no
    // subscriber. After the `isCurrent` guard on purpose — a superseded run
    // must never release the live run's recording.
    task.blob = null;
    // The timing props are what make a slow success visible: without them a
    // 2-minute upload that hung twice reads exactly like a 3-second one.
    run.track("upload_succeeded", {
      ...eventBase(run),
      duration_ms: durationMs,
      retry_count: run.retryCount,
      stall_count: run.stallCount,
    });
  } catch (error) {
    // The abort reason (from an abort check or a rejected sleep) is the only
    // throw this path expects; every mutation and driver failure is returned,
    // not thrown. A superseded run stays silent.
    if (isAbortReason(error, signal)) {
      if (isCurrent(run)) patch({ status: { phase: "aborted" } });
      return;
    }
    // Anything else is a bug in this file's own control flow, and swallowing it
    // would present as a progress bar frozen at 100% forever: no error, no
    // event, nothing to search for. Surface it as a terminal failure — which
    // also emits `upload_failed` with `failure_kind: "unexpected"` — and log
    // it, since `detail` is never rendered.
    console.error("[upload] unexpected error in the upload run", error);
    setFailed(run, {
      kind: "unexpected",
      messageKey: GENERIC_ERROR_MESSAGE_KEYS.unknown,
      canRetry: true,
      detail: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
    });
  }
}
